import React, { useRef, useEffect, useState, useCallback, useMemo } from 'react';

// Remove Monaco imports entirely - we'll build a custom solution
import { Toolbar } from './Toolbar';
import { Preview } from './Preview';
import { copyToClipboard } from '../../utils/download';
import {
  formatInText,
  formatInTextNarrative,
  formatReference,
  mapCitationToSourceMeta
} from '../../lib/apaSlideFormatter.js';
import { getApaCitationKey, hasApaCitationContent } from '../../utils/apaCitationUtils';
import { readSavedApaCitations, writeSavedApaCitations } from '../../utils/apaLibraryUtils';
import './codeblock.css';

export type CodeBlockMode = 'displayCode' | 'renderRich';
export type CodeLanguage = 'html' | 'css' | 'javascript' | 'json' | 'text';

export type CodeCitationMode = 'code' | 'underlying' | 'ai';

export type CodeCitationRecord = {
  id?: string;
  citationId?: string;
  citationMode?: CodeCitationMode;
  sourceType?: string;
  author?: string;
  authorFirstName?: string;
  authorLastName?: string;
  contributors?: Array<{
    firstName?: string;
    lastName?: string;
    name?: string;
    organization?: string;
    role?: string;
  }>;
  year?: string;
  date?: string;
  title?: string;
  source?: string;
  publisher?: string;
  siteName?: string;
  version?: string;
  url?: string;
  doi?: string;
  volume?: string;
  issue?: string;
  pages?: string;
  retrievalDateEnabled?: boolean;
  retrievalDate?: string;
  prompt?: string;
  notes?: string;
  computed?: {
    apaReference?: string;
    apaInTextParenthetical?: string;
    apaInTextNarrative?: string;
  };
};

export type CodeBlockState = {
  id: string;
  content: string;
  language: CodeLanguage;
  customCss?: string;
  mode: CodeBlockMode;
  citation?: string | CodeCitationRecord;
  options: {
    softWrap: boolean;
    showLineNumbers: boolean;
    allowScripts: boolean;
  };
  updatedAt: number;
};

export type CodeBlockProps = {
  id: string;
  initial?: Partial<CodeBlockState>;
  readOnly?: boolean;
  onChange?: (s: CodeBlockState) => void;
};

const buildCitationLabel = (citation: Record<string, unknown>) => {
  const meta = mapCitationToSourceMeta(citation);
  const reference = formatReference(meta);
  if (reference) return reference;
  const fallbackParts = [
    citation.author,
    citation.title,
    citation.source,
    citation.url
  ].filter((value) => typeof value === 'string' && value.trim().length > 0);
  return fallbackParts.join(' ') || 'Saved citation';
};

const defaultState = (id: string): CodeBlockState => ({
  id,
  content: '',
  language: 'html',
  customCss: '',
  mode: 'displayCode',
  citation: undefined,
  options: { softWrap: false, showLineNumbers: true, allowScripts: false },
  updatedAt: Date.now(),
});

const normalizeCitationDraft = (citation?: string | CodeCitationRecord): CodeCitationRecord => {
  if (!citation || typeof citation === 'string') {
    return {
      citationMode: 'code',
      sourceType: 'software',
      title: '',
      author: '',
      url: '',
      version: '',
      publisher: '',
      date: ''
    };
  }
  return {
    citationId: citation.citationId || citation.id || '',
    citationMode: citation.citationMode || 'code',
    sourceType: citation.sourceType || (citation.citationMode === 'underlying' ? 'webpage' : 'software'),
    title: citation.title || '',
    author: citation.author || '',
    url: citation.url || '',
    version: citation.version || '',
    publisher: citation.publisher || '',
    date: citation.date || '',
    year: citation.year || '',
    doi: citation.doi || '',
    volume: citation.volume || '',
    issue: citation.issue || '',
    pages: citation.pages || '',
    retrievalDateEnabled: Boolean(citation.retrievalDateEnabled),
    retrievalDate: citation.retrievalDate || '',
    contributors: Array.isArray(citation.contributors) ? citation.contributors : []
  };
};

function autocloseSimpleSpan(content: string): string {
  const m = content.match(/(<span\b[^>]*class="[^"]*"[^>]*>)([^<]*?)$/i);
  if (m && !content.toLowerCase().trim().endsWith('</span>')) {
    return content + '</span>';
  }
  return content;
}

// Custom Code Editor Component Props Interface
interface CustomCodeEditorProps {
  value: string;
  onChange: (value: string) => void;
  language: CodeLanguage;
  readOnly?: boolean;
  showLineNumbers?: boolean;
  softWrap?: boolean;
  height?: number;
  textareaRef?: React.RefObject<HTMLTextAreaElement>;
}

// Custom Code Editor Component
const CustomCodeEditor: React.FC<CustomCodeEditorProps> = ({
  value,
  onChange,
  language,
  readOnly = false,
  showLineNumbers = true,
  softWrap = false,
  height = 360,
  textareaRef: forwardedRef,
}) => {
  const internalRef = useRef<HTMLTextAreaElement>(null);
  const textareaRef = forwardedRef ?? internalRef;
  const lineNumbersRef = useRef<HTMLDivElement>(null);
  const [lineCount, setLineCount] = useState(1);

  // Update line numbers when content changes
  useEffect(() => {
    if (showLineNumbers && value) {
      const lines = value.split('\n').length;
      setLineCount(lines);
    }
  }, [value, showLineNumbers]);

  // Sync scroll between textarea and line numbers
  const handleScroll = useCallback(() => {
    if (lineNumbersRef.current && textareaRef.current) {
      lineNumbersRef.current.scrollTop = textareaRef.current.scrollTop;
    }
  }, []);

  // Handle tab insertion
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Tab') {
      e.preventDefault();
      const textarea = e.currentTarget;
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const newValue = value.substring(0, start) + '  ' + value.substring(end);
      onChange(newValue);

      // Set cursor position after the inserted tab
      setTimeout(() => {
        textarea.selectionStart = textarea.selectionEnd = start + 2;
      }, 0);
    }
  };

  // Ensure pasted content inserts at the cursor position
  const handlePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const textarea = e.currentTarget;
    const pasted = e.clipboardData.getData('text');
    if (!pasted) return;
    e.preventDefault();
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const newValue = value.substring(0, start) + pasted + value.substring(end);
    onChange(newValue);
    requestAnimationFrame(() => {
      const pos = start + pasted.length;
      textarea.selectionStart = textarea.selectionEnd = pos;
    });
  };

  const lineNumbers = showLineNumbers 
    ? Array.from({ length: Math.max(lineCount, 1) }, (_, i) => i + 1)
    : [];

  return (
    <div 
      className="custom-code-editor" 
      style={{ height }}
    >
      <div className="editor-container">
        {showLineNumbers && (
          <div
            ref={lineNumbersRef}
            className="editor-line-numbers"
            style={{ height }}
          >
            {lineNumbers.map(num => (
              <div key={num} className="editor-line-number">
                {num}
              </div>
            ))}
          </div>
        )}
        
        <textarea
          ref={textareaRef}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          onScroll={handleScroll}
          readOnly={readOnly}
          className={`code-textarea ${language} ${softWrap ? 'wrap' : 'no-wrap'} ${showLineNumbers ? 'with-line-numbers' : ''}`}
          style={{ height }}
          placeholder="Enter your code here..."
          spellCheck={false}
        />
      </div>
    </div>
  );
};

export default function CodeBlock({ id, initial, readOnly, onChange }: CodeBlockProps) {
  // Build a stable initial state so we can derive the initial editing mode
  const initialRef = useRef<CodeBlockState>({ ...defaultState(id), ...initial });
  const [state, setState] = useState<CodeBlockState>(initialRef.current);
  // If there's no content yet, open the editor by default so it "loads" visibly
  const [editing, setEditing] = useState(!initialRef.current.content);
  const [copied, setCopied] = useState(false);
  const [height, setHeight] = useState(360);
  const [cssOpen, setCssOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [cssSearchTerm, setCssSearchTerm] = useState('');
  const [cssHeight, setCssHeight] = useState(200);
  const [citationOpen, setCitationOpen] = useState(false);
  const [citationTab, setCitationTab] = useState<'create' | 'library'>('create');
  const [citationMode, setCitationMode] = useState<CodeCitationMode>('code');
  const [citationDraft, setCitationDraft] = useState<CodeCitationRecord>(normalizeCitationDraft(initialRef.current.citation));
  const [savedApaCitations, setSavedApaCitations] = useState<Record<string, unknown>[]>([]);
  const [libraryQuery, setLibraryQuery] = useState('');
  const [libraryFilter, setLibraryFilter] = useState('all');
  const editorRef = useRef<HTMLTextAreaElement>(null);
  const cssEditorRef = useRef<HTMLTextAreaElement>(null);

  // When the block becomes read-only (preview mode), ensure the editor closes
  useEffect(() => {
    if (readOnly) {
      setEditing(false);
      setSearchOpen(false);
    }
  }, [readOnly]);

  // Track latest onChange and debounce timers without triggering renders
  const onChangeRef = useRef(onChange);
  React.useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);
  const debounceRef = useRef<number>();

  // Internal state updater; schedules parent callback outside of render
  const update = useCallback((updater: (s: CodeBlockState) => CodeBlockState) => {
    setState((prev) => {
      const next = updater(prev);
      const fullState: CodeBlockState = {
        id: next.id,
        content: next.content,
        language: next.language,
        customCss: next.customCss,
        mode: next.mode,
        citation: next.citation,
        options: next.options,
        updatedAt: Date.now(),
      };
      if (debounceRef.current) {
        window.clearTimeout(debounceRef.current);
      }
      debounceRef.current = window.setTimeout(() => {
        onChangeRef.current?.(fullState);
      }, 150);
      return next;
    });
  }, []);

  useEffect(() => () => {
    if (debounceRef.current) {
      window.clearTimeout(debounceRef.current);
    }
  }, []);

  const onToggleEdit = useCallback(() => {
    setEditing((v) => !v);
    setSearchOpen(false);
  }, []);

  const onToggleCss = useCallback(() => {
    setCssOpen((v) => !v);
  }, []);

  const onToggleSearch = useCallback(() => {
    setSearchOpen((v) => !v);
    setSearchTerm('');
    setCssSearchTerm('');
  }, []);

  const openCitationModal = useCallback(() => {
    const normalized = normalizeCitationDraft(state.citation);
    setCitationDraft(normalized);
    setCitationMode(normalized.citationMode || 'code');
    setCitationTab('create');
    setCitationOpen(true);
  }, [state.citation]);

  const closeCitationModal = useCallback(() => {
    setCitationOpen(false);
  }, []);

  useEffect(() => {
    if (citationOpen) {
      setSavedApaCitations(readSavedApaCitations());
    }
  }, [citationOpen]);

  const buildComputedCitation = useCallback((draft: CodeCitationRecord) => {
    const resolvedMode = draft.citationMode || citationMode;
    const resolvedSourceType = resolvedMode === 'code'
      ? 'software'
      : resolvedMode === 'ai'
        ? 'ai'
        : (draft.sourceType || 'webpage');
    const meta = mapCitationToSourceMeta({ ...draft, citationMode: resolvedMode, sourceType: resolvedSourceType });
    return {
      ...draft,
      citationMode: resolvedMode,
      sourceType: resolvedSourceType,
      citationId: draft.citationId || draft.id || '',
      computed: {
        apaReference: formatReference(meta) || '',
        apaInTextParenthetical: formatInText(meta) || '',
        apaInTextNarrative: formatInTextNarrative(meta) || ''
      }
    };
  }, [citationMode]);

  const filteredLibrary = useMemo(() => {
    const query = libraryQuery.trim().toLowerCase();
    return (savedApaCitations || []).filter((citation) => {
      if (libraryFilter !== 'all' && citation.sourceType !== libraryFilter && citation.type !== libraryFilter) {
        return false;
      }
      if (!query) return true;
      const label = buildCitationLabel(citation);
      return label.toLowerCase().includes(query);
    });
  }, [savedApaCitations, libraryFilter, libraryQuery]);

  const savedCitationOptions = useMemo(
    () => (filteredLibrary || []).map((citation) => ({
      citation,
      label: buildCitationLabel(citation)
    })),
    [filteredLibrary]
  );

  const saveCitation = useCallback(() => {
    const computed = buildComputedCitation({ ...citationDraft, citationMode });
    update((s) => ({ ...s, citation: computed }));
    setCitationOpen(false);
  }, [buildComputedCitation, citationDraft, citationMode, update]);

  const saveDraftToLibrary = useCallback(() => {
    const computed = buildComputedCitation({ ...citationDraft, citationMode });
    if (!hasApaCitationContent(computed)) {
      window.alert('Add citation details before saving.');
      return;
    }
    const existing = Array.isArray(savedApaCitations) ? savedApaCitations : [];
    const key = getApaCitationKey(computed);
    const existingIndex = existing.findIndex((entry) => entry.citationId && entry.citationId === computed.citationId);
    const duplicateIndex = existing.findIndex((entry, index) =>
      index !== existingIndex && getApaCitationKey(entry) === key
    );
    if (duplicateIndex !== -1) {
      window.alert('Citation already saved.');
      return;
    }
    const updated = existingIndex >= 0
      ? existing.map((entry, index) => (index === existingIndex ? computed : entry))
      : [...existing, { ...computed, citationId: computed.citationId || computed.id }];
    setSavedApaCitations(updated);
    writeSavedApaCitations(updated);
  }, [buildComputedCitation, citationDraft, citationMode, savedApaCitations]);

  const handleLibraryApply = useCallback((citation: Record<string, unknown>) => {
    const normalized = normalizeCitationDraft(citation as CodeCitationRecord);
    const computed = buildComputedCitation({ ...normalized, citationMode: normalized.citationMode || 'underlying' });
    update((s) => ({ ...s, citation: computed }));
    setCitationOpen(false);
  }, [buildComputedCitation, update]);

  const handleLibraryEdit = useCallback((citation: Record<string, unknown>) => {
    const normalized = normalizeCitationDraft(citation as CodeCitationRecord);
    setCitationDraft(normalized);
    setCitationMode(normalized.citationMode || 'underlying');
    setCitationTab('create');
  }, []);

  const handleCitationFieldChange = useCallback((field: keyof CodeCitationRecord, value: string | boolean) => {
    setCitationDraft((prev) => ({ ...prev, [field]: value }));
  }, []);

  const validationErrors = useMemo(() => {
    const errors: Record<string, string> = {};
    if (citationMode === 'code') {
      if (!citationDraft.author?.trim() && !citationDraft.publisher?.trim()) {
        errors.author = 'Author or organization is required.';
      }
      if (!citationDraft.title?.trim()) {
        errors.title = 'Software title is required.';
      }
      if (!citationDraft.url?.trim()) {
        errors.url = 'URL is required.';
      }
    }
    if (citationMode === 'ai') {
      if (!citationDraft.publisher?.trim()) {
        errors.publisher = 'Organization is required.';
      }
      if (!citationDraft.source?.trim()) {
        errors.source = 'Tool name is required.';
      }
      if (!citationDraft.title?.trim()) {
        errors.title = 'Prompt description is required.';
      }
      if (!citationDraft.date?.trim() && !citationDraft.year?.trim()) {
        errors.date = 'Date of generation is required.';
      }
    }
    if (citationMode === 'underlying') {
      if (!citationDraft.title?.trim()) {
        errors.title = 'Title is required.';
      }
      if (!citationDraft.author?.trim() && !citationDraft.publisher?.trim()) {
        errors.author = 'Author or organization is required.';
      }
      if (!citationDraft.date?.trim() && !citationDraft.year?.trim()) {
        errors.date = 'Date or year is required.';
      }
      if (citationDraft.sourceType === 'journal') {
        if (!citationDraft.volume?.trim()) {
          errors.volume = 'Volume is required.';
        }
        if (!citationDraft.pages?.trim()) {
          errors.pages = 'Pages are required.';
        }
      }
    }
    return errors;
  }, [citationDraft, citationMode]);

  const isCitationValid = Object.keys(validationErrors).length === 0;


  const searchIndexRef = useRef(-1);

  const scrollToMatch = (textarea: HTMLTextAreaElement, index: number) => {
    const before = textarea.value.slice(0, index);
    const lineHeight = parseInt(
      window.getComputedStyle(textarea).lineHeight || '16',
      10
    );
    const line = before.split('\n').length - 1;
    const target = line * lineHeight;
    textarea.scrollTop = target;
    textarea.dispatchEvent(new Event('scroll'));
  };

  const handleSearchNext = useCallback(() => {
    const textarea = editorRef.current;
    if (!textarea || !searchTerm) return;
    const content = textarea.value;
    const lowerContent = content.toLowerCase();
    const term = searchTerm.toLowerCase();
    let idx = lowerContent.indexOf(term, searchIndexRef.current + 1);
    if (idx === -1) idx = lowerContent.indexOf(term);
    if (idx !== -1) {
      searchIndexRef.current = idx;
      textarea.setSelectionRange(idx, idx + searchTerm.length);
      textarea.focus();
      scrollToMatch(textarea, idx);
    }
  }, [searchTerm, state.content]);

  const handleSearchPrev = useCallback(() => {
    const textarea = editorRef.current;
    if (!textarea || !searchTerm) return;
    const content = textarea.value;
    const lowerContent = content.toLowerCase();
    const term = searchTerm.toLowerCase();
    let idx = lowerContent.lastIndexOf(term, searchIndexRef.current - 1);
    if (idx === -1) idx = lowerContent.lastIndexOf(term);
    if (idx !== -1) {
      searchIndexRef.current = idx;
      textarea.setSelectionRange(idx, idx + searchTerm.length);
      textarea.focus();
      scrollToMatch(textarea, idx);
    }
  }, [searchTerm, state.content]);

  useEffect(() => {
    searchIndexRef.current = -1;
  }, [searchTerm, state.content]);

  function handleCopy() {
    copyToClipboard(state.content);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }

  const handleContentChange = (content: string) => {
    const safe =
      state.language === 'html' && state.mode === 'renderRich'
        ? autocloseSimpleSpan(content)
        : content;
    update((s) => ({ ...s, content: safe }));
  };

  const handleCssChange = (css: string) => {
    update((s) => ({ ...s, customCss: css }));
  };

  const cssSearchIndexRef = useRef(-1);

  const handleCssSearchNext = useCallback(() => {
    const textarea = cssEditorRef.current;
    if (!textarea || !cssSearchTerm) return;
    const content = textarea.value;
    const lowerContent = content.toLowerCase();
    const term = cssSearchTerm.toLowerCase();
    let idx = lowerContent.indexOf(term, cssSearchIndexRef.current + 1);
    if (idx === -1) idx = lowerContent.indexOf(term);
    if (idx !== -1) {
      cssSearchIndexRef.current = idx;
      textarea.setSelectionRange(idx, idx + cssSearchTerm.length);
      textarea.focus();
      scrollToMatch(textarea, idx);
    }
  }, [cssSearchTerm, state.customCss]);

  const handleCssSearchPrev = useCallback(() => {
    const textarea = cssEditorRef.current;
    if (!textarea || !cssSearchTerm) return;
    const content = textarea.value;
    const lowerContent = content.toLowerCase();
    const term = cssSearchTerm.toLowerCase();
    let idx = lowerContent.lastIndexOf(term, cssSearchIndexRef.current - 1);
    if (idx === -1) idx = lowerContent.lastIndexOf(term);
    if (idx !== -1) {
      cssSearchIndexRef.current = idx;
      textarea.setSelectionRange(idx, idx + cssSearchTerm.length);
      textarea.focus();
      scrollToMatch(textarea, idx);
    }
  }, [cssSearchTerm, state.customCss]);

  useEffect(() => {
    cssSearchIndexRef.current = -1;
  }, [cssSearchTerm, state.customCss]);

  const onResizeStart = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    const startY = e.clientY;
    const startH = height;
    function onMove(ev: MouseEvent) {
      setHeight(Math.max(200, startH + (ev.clientY - startY)));
    }
    function onUp() {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    }
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  const onCssResizeStart = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    const startY = e.clientY;
    const startH = cssHeight;
    function onMove(ev: MouseEvent) {
      setCssHeight(Math.max(200, startH + (ev.clientY - startY)));
    }
    function onUp() {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    }
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  return (
    <div
      className="codeblock"
      data-mode={state.mode}
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      {!readOnly && (
        <Toolbar
          state={state}
          editing={editing}
          cssOpen={cssOpen}
          copied={copied}
          searchOpen={searchOpen}
          onState={update}
          onToggleEdit={onToggleEdit}
          onCopy={handleCopy}
          onToggleCss={onToggleCss}
          onToggleSearch={onToggleSearch}
          onOpenCitation={openCitationModal}
        />
      )}
      {editing && searchOpen && (
        <div className="search-bar">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search..."
          />
          <button onClick={handleSearchPrev} aria-label="Previous">
            ↑
          </button>
          <button onClick={handleSearchNext} aria-label="Next">
            ↓
          </button>
        </div>
      )}
      <div className="codeblock-body">
        {editing ? (
          <>
            <div className="editor-shell" aria-label="Code editor">
              <CustomCodeEditor
                value={state.content}
                onChange={handleContentChange}
                language={state.language}
                readOnly={readOnly}
                showLineNumbers={state.options.showLineNumbers}
                softWrap={state.options.softWrap}
                height={height}
                textareaRef={editorRef}
              />
              <div className="resize-handle" onMouseDown={onResizeStart} />
            </div>
            {cssOpen && (
              <div className="editor-shell" aria-label="Custom CSS editor" style={{ marginTop: 8 }}>
                {searchOpen && (
                  <div className="search-bar">
                    <input
                      type="text"
                      value={cssSearchTerm}
                      onChange={(e) => setCssSearchTerm(e.target.value)}
                      placeholder="Search..."
                    />
                    <button onClick={handleCssSearchPrev} aria-label="Previous">
                      ↑
                    </button>
                    <button onClick={handleCssSearchNext} aria-label="Next">
                      ↓
                    </button>
                  </div>
                )}
                <CustomCodeEditor
                  value={state.customCss || ''}
                  onChange={handleCssChange}
                  language="css"
                  readOnly={readOnly}
                  showLineNumbers={state.options.showLineNumbers}
                  softWrap={state.options.softWrap}
                  height={cssHeight}
                  textareaRef={cssEditorRef}
                />
                <div className="resize-handle" onMouseDown={onCssResizeStart} />
              </div>
            )}
          </>
        ) : (
          <Preview state={state} showMeta={!readOnly} />
        )}
      </div>
      {state.citation && (
        <div className="codeblock-citation" aria-label="Code citation">
          {typeof state.citation === 'string'
            ? state.citation
            : state.citation.computed?.apaReference
              || state.citation.computed?.apaInTextParenthetical
              || formatReference(mapCitationToSourceMeta(state.citation))}
        </div>
      )}
      <span className="sr-only" aria-live="polite">
        {copied ? 'Copied to clipboard' : ''}
      </span>
      {citationOpen && (
        <div className="codeblock-modal" role="dialog" aria-modal="true" aria-label="Code citation">
          <div className="codeblock-modal-backdrop" onClick={closeCitationModal} />
          <div className="codeblock-modal-content">
            <div className="codeblock-modal-header">
              <h3>Add code citation</h3>
              <button type="button" onClick={closeCitationModal} aria-label="Close">
                ✕
              </button>
            </div>
            <p className="codeblock-modal-helper">Add a reference or citation for the code or rendered HTML.</p>
            <div className="codeblock-modal-tabs">
              <button
                type="button"
                onClick={() => setCitationTab('create')}
                className={citationTab === 'create' ? 'active' : ''}
              >
                Create / Edit
              </button>
              <button
                type="button"
                onClick={() => setCitationTab('library')}
                className={citationTab === 'library' ? 'active' : ''}
              >
                Saved Library
              </button>
            </div>
            {citationTab === 'create' ? (
              <div className="codeblock-citation-form">
                <label className="codeblock-field">
                  <span>Citation mode</span>
                  <select
                    value={citationMode}
                    onChange={(e) => setCitationMode(e.target.value as CodeCitationMode)}
                  >
                    <option value="code">Code / Software / Repository</option>
                    <option value="underlying">Underlying source for this code</option>
                    <option value="ai">LLM-generated code (AI tool)</option>
                  </select>
                </label>
                {citationMode === 'code' && (
                  <div className="codeblock-grid">
                    <label className="codeblock-field">
                      <span>Author or organization *</span>
                      <input
                        type="text"
                        value={citationDraft.author || ''}
                        onChange={(e) => handleCitationFieldChange('author', e.target.value)}
                        placeholder="OpenAI, Mozilla, or Author Name"
                      />
                      {validationErrors.author && <span className="codeblock-error">{validationErrors.author}</span>}
                    </label>
                    <label className="codeblock-field">
                      <span>Year or date</span>
                      <input
                        type="text"
                        value={citationDraft.year || citationDraft.date || ''}
                        onChange={(e) => handleCitationFieldChange('year', e.target.value)}
                        placeholder="2024 or 2024-03-12"
                      />
                    </label>
                    <label className="codeblock-field">
                      <span>Software title *</span>
                      <input
                        type="text"
                        value={citationDraft.title || ''}
                        onChange={(e) => handleCitationFieldChange('title', e.target.value)}
                        placeholder="Library or tool name"
                      />
                      {validationErrors.title && <span className="codeblock-error">{validationErrors.title}</span>}
                    </label>
                    <label className="codeblock-field">
                      <span>Version</span>
                      <input
                        type="text"
                        value={citationDraft.version || ''}
                        onChange={(e) => handleCitationFieldChange('version', e.target.value)}
                        placeholder="v2.1.0"
                      />
                    </label>
                    <label className="codeblock-field">
                      <span>Publisher/Host</span>
                      <input
                        type="text"
                        value={citationDraft.publisher || ''}
                        onChange={(e) => handleCitationFieldChange('publisher', e.target.value)}
                        placeholder="GitHub, npm, vendor"
                      />
                    </label>
                    <label className="codeblock-field">
                      <span>URL *</span>
                      <input
                        type="text"
                        value={citationDraft.url || ''}
                        onChange={(e) => handleCitationFieldChange('url', e.target.value)}
                        placeholder="https://example.com"
                      />
                      {validationErrors.url && <span className="codeblock-error">{validationErrors.url}</span>}
                    </label>
                    <label className="codeblock-field">
                      <span>DOI</span>
                      <input
                        type="text"
                        value={citationDraft.doi || ''}
                        onChange={(e) => handleCitationFieldChange('doi', e.target.value)}
                        placeholder="10.0000/xyz123"
                      />
                    </label>
                    <label className="codeblock-field">
                      <span>Include retrieval date</span>
                      <select
                        value={citationDraft.retrievalDateEnabled ? 'yes' : 'no'}
                        onChange={(e) => handleCitationFieldChange('retrievalDateEnabled', e.target.value === 'yes')}
                      >
                        <option value="no">No</option>
                        <option value="yes">Yes</option>
                      </select>
                    </label>
                    {citationDraft.retrievalDateEnabled && (
                      <label className="codeblock-field">
                        <span>Retrieval date</span>
                        <input
                          type="date"
                          value={citationDraft.retrievalDate || ''}
                          onChange={(e) => handleCitationFieldChange('retrievalDate', e.target.value)}
                        />
                      </label>
                    )}
                  </div>
                )}
                {citationMode === 'underlying' && (
                  <div className="codeblock-grid">
                    <label className="codeblock-field">
                      <span>Underlying source type</span>
                      <select
                        value={citationDraft.sourceType || 'webpage'}
                        onChange={(e) => handleCitationFieldChange('sourceType', e.target.value)}
                      >
                        <option value="webpage">Webpage</option>
                        <option value="book">Book</option>
                        <option value="journal">Journal article</option>
                        <option value="video">Video</option>
                        <option value="image">Image/Figure</option>
                        <option value="table">Table</option>
                      </select>
                    </label>
                    <label className="codeblock-field">
                      <span>Author or organization *</span>
                      <input
                        type="text"
                        value={citationDraft.author || ''}
                        onChange={(e) => handleCitationFieldChange('author', e.target.value)}
                        placeholder="Author or organization"
                      />
                      {validationErrors.author && <span className="codeblock-error">{validationErrors.author}</span>}
                    </label>
                    <label className="codeblock-field">
                      <span>Date or year *</span>
                      <input
                        type="text"
                        value={citationDraft.date || citationDraft.year || ''}
                        onChange={(e) => handleCitationFieldChange('date', e.target.value)}
                        placeholder="2024-04-02 or 2024"
                      />
                      {validationErrors.date && <span className="codeblock-error">{validationErrors.date}</span>}
                    </label>
                    <label className="codeblock-field">
                      <span>Title *</span>
                      <input
                        type="text"
                        value={citationDraft.title || ''}
                        onChange={(e) => handleCitationFieldChange('title', e.target.value)}
                        placeholder="Source title"
                      />
                      {validationErrors.title && <span className="codeblock-error">{validationErrors.title}</span>}
                    </label>
                    <label className="codeblock-field">
                      <span>Source / Publisher</span>
                      <input
                        type="text"
                        value={citationDraft.source || ''}
                        onChange={(e) => handleCitationFieldChange('source', e.target.value)}
                        placeholder="Journal, publisher, website"
                      />
                    </label>
                    {citationDraft.sourceType === 'book' && (
                      <label className="codeblock-field">
                        <span>Page</span>
                        <input
                          type="text"
                          value={citationDraft.pages || ''}
                          onChange={(e) => handleCitationFieldChange('pages', e.target.value)}
                          placeholder="123"
                        />
                      </label>
                    )}
                    {citationDraft.sourceType === 'journal' && (
                      <>
                        <label className="codeblock-field">
                          <span>Volume</span>
                          <input
                            type="text"
                            value={citationDraft.volume || ''}
                            onChange={(e) => handleCitationFieldChange('volume', e.target.value)}
                            placeholder="12"
                          />
                          {validationErrors.volume && <span className="codeblock-error">{validationErrors.volume}</span>}
                        </label>
                        <label className="codeblock-field">
                          <span>Issue</span>
                          <input
                            type="text"
                            value={citationDraft.issue || ''}
                            onChange={(e) => handleCitationFieldChange('issue', e.target.value)}
                            placeholder="3"
                          />
                        </label>
                        <label className="codeblock-field">
                          <span>Pages</span>
                          <input
                            type="text"
                            value={citationDraft.pages || ''}
                            onChange={(e) => handleCitationFieldChange('pages', e.target.value)}
                            placeholder="45-67"
                          />
                          {validationErrors.pages && <span className="codeblock-error">{validationErrors.pages}</span>}
                        </label>
                      </>
                    )}
                    <label className="codeblock-field">
                      <span>URL</span>
                      <input
                        type="text"
                        value={citationDraft.url || ''}
                        onChange={(e) => handleCitationFieldChange('url', e.target.value)}
                        placeholder="https://example.com"
                      />
                    </label>
                    <label className="codeblock-field">
                      <span>DOI</span>
                      <input
                        type="text"
                        value={citationDraft.doi || ''}
                        onChange={(e) => handleCitationFieldChange('doi', e.target.value)}
                        placeholder="10.0000/xyz123"
                      />
                    </label>
                  </div>
                )}
                {citationMode === 'ai' && (
                  <div className="codeblock-grid">
                    <label className="codeblock-field">
                      <span>Organization *</span>
                      <input
                        type="text"
                        value={citationDraft.publisher || ''}
                        onChange={(e) => handleCitationFieldChange('publisher', e.target.value)}
                        placeholder="OpenAI"
                      />
                      {validationErrors.publisher && <span className="codeblock-error">{validationErrors.publisher}</span>}
                    </label>
                    <label className="codeblock-field">
                      <span>Tool name *</span>
                      <input
                        type="text"
                        value={citationDraft.source || ''}
                        onChange={(e) => handleCitationFieldChange('source', e.target.value)}
                        placeholder="ChatGPT"
                      />
                      {validationErrors.source && <span className="codeblock-error">{validationErrors.source}</span>}
                    </label>
                    <label className="codeblock-field">
                      <span>Date of generation *</span>
                      <input
                        type="text"
                        value={citationDraft.date || citationDraft.year || ''}
                        onChange={(e) => handleCitationFieldChange('date', e.target.value)}
                        placeholder="2024-03-12"
                      />
                      {validationErrors.date && <span className="codeblock-error">{validationErrors.date}</span>}
                    </label>
                    <label className="codeblock-field">
                      <span>Prompt description *</span>
                      <input
                        type="text"
                        value={citationDraft.title || ''}
                        onChange={(e) => handleCitationFieldChange('title', e.target.value)}
                        placeholder="Describe the prompt"
                      />
                      {validationErrors.title && <span className="codeblock-error">{validationErrors.title}</span>}
                    </label>
                    <label className="codeblock-field">
                      <span>Model/version</span>
                      <input
                        type="text"
                        value={citationDraft.version || ''}
                        onChange={(e) => handleCitationFieldChange('version', e.target.value)}
                        placeholder="GPT-4"
                      />
                    </label>
                    <label className="codeblock-field">
                      <span>URL</span>
                      <input
                        type="text"
                        value={citationDraft.url || ''}
                        onChange={(e) => handleCitationFieldChange('url', e.target.value)}
                        placeholder="https://chat.openai.com/"
                      />
                    </label>
                  </div>
                )}
                <div className="codeblock-preview">
                  <div>
                    <strong>APA Reference:</strong>{' '}
                    {formatReference(mapCitationToSourceMeta({ ...citationDraft, citationMode })) || 'Complete the fields to preview.'}
                  </div>
                  <div>
                    <strong>In-text (Parenthetical):</strong>{' '}
                    {formatInText(mapCitationToSourceMeta({ ...citationDraft, citationMode })) || 'Complete the fields to preview.'}
                  </div>
                  <div>
                    <strong>In-text (Narrative):</strong>{' '}
                    {formatInTextNarrative(mapCitationToSourceMeta({ ...citationDraft, citationMode })) || 'Complete the fields to preview.'}
                  </div>
                </div>
              </div>
            ) : (
              <div className="codeblock-citation-library">
                <div className="codeblock-citation-library-header">
                  <span>Saved citations</span>
                  <span className="codeblock-citation-library-count">
                    {savedApaCitations.length} saved
                  </span>
                </div>
                <div className="codeblock-library-controls">
                  <input
                    type="text"
                    value={libraryQuery}
                    onChange={(e) => setLibraryQuery(e.target.value)}
                    placeholder="Search saved citations..."
                  />
                  <select value={libraryFilter} onChange={(e) => setLibraryFilter(e.target.value)}>
                    <option value="all">All types</option>
                    <option value="book">Book</option>
                    <option value="journal">Journal</option>
                    <option value="webpage">Webpage</option>
                    <option value="video">Video</option>
                    <option value="image">Image</option>
                    <option value="software">Software</option>
                    <option value="ai">AI</option>
                  </select>
                </div>
                {savedCitationOptions.length > 0 ? (
                  <div className="codeblock-citation-library-list">
                    {savedCitationOptions.map(({ label, citation }, index) => (
                      <div
                        key={`code-citation-option-${index}`}
                        className="codeblock-citation-library-item"
                      >
                        <span className="codeblock-citation-library-text">{label}</span>
                        <div className="codeblock-citation-library-actions">
                          <button type="button" onClick={() => handleLibraryApply(citation)}>
                            Use
                          </button>
                          <button type="button" onClick={() => handleLibraryEdit(citation)}>
                            Edit
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="codeblock-citation-library-empty">No saved citations match your search.</p>
                )}
              </div>
            )}
            <div className="codeblock-modal-actions">
              {citationTab === 'create' && (
                <button type="button" className="secondary" onClick={saveDraftToLibrary}>
                  Save to Library
                </button>
              )}
              <button type="button" className="secondary" onClick={closeCitationModal}>
                Cancel
              </button>
              <button type="button" className="primary" onClick={saveCitation} disabled={!isCitationValid}>
                Save citation
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
